import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PracticeAreaConfigService } from '../../../../../services/practiceAreaConfigService';
import CourtIntelligenceWizardPanel from './CourtIntelligenceWizardPanel';
import CourtReviewQueuePanel from './CourtReviewQueuePanel';
import CrawlerStatusBar from './CrawlerStatusBar';
import TemplateCard from './TemplateCard';
import TemplateFilterBar from './TemplateFilterBar';
import {
  approveCourtIntelligenceReviewItem,
  getCourtIntelligenceStatus,
  getCourtIntelligenceReviewQueue,
  forkTaskTemplateAdmin,
  listTaskTemplatesAdmin,
  rejectCourtIntelligenceReviewItem,
  synthesizeTemplateFromCourtData,
  synthesizeSubtypeTemplates,
  triggerCourtIntelligenceManualRun,
  type CourtReviewQueueItem,
  type CourtIntelligenceStatusResponse,
  type TaskTemplateListFilters,
  type TaskTemplateListItem,
  type TaskTemplatePagination,
} from './templateManagementService';

interface TemplateBrowserProps {
  onSelectTemplate: (templateId: string) => void;
  onEditTemplate: (templateId: string) => void;
  onArchiveTemplate?: (template: import('./templateManagementService').TaskTemplateListItem) => void;
}

const DEFAULT_PAGINATION: TaskTemplatePagination = {
  page: 1,
  pageSize: 50,
  total: 0,
  totalPages: 0,
};

function extractJurisdictionCode(value?: string): string | undefined {
  const source = String(value || '').trim().toUpperCase();
  if (!source) return undefined;
  const exact = /^[A-Z]{2}-\d{5,6}$/.test(source) ? source : null;
  if (exact) return exact;
  const match = source.match(/[A-Z]{2}-\d{5,6}/);
  return match ? match[0] : undefined;
}

function normalizePracticeAreaForExtraction(value?: string): string | undefined {
  const source = String(value || '').trim().toLowerCase();
  if (!source) return undefined;
  return source.replace(/\s+/g, '_');
}

function resolvePracticeAreaName(
  wizardValue: string,
  knownAreas: Array<{ name: string; displayName: string }>,
): string {
  const exact = knownAreas.find((pa) => pa.name === wizardValue);
  if (exact) return exact.name;
  const normalized = wizardValue.trim().toLowerCase().replace(/\s+/g, '_');
  const byNormalized = knownAreas.find(
    (pa) => pa.name.toLowerCase().replace(/\s+/g, '_') === normalized,
  );
  return byNormalized ? byNormalized.name : wizardValue;
}

function formatTemplateKind(kind?: string): string {
  if (!kind) return '';
  return kind.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Hierarchy grouping: master → subtypes → firm variants
// ---------------------------------------------------------------------------
interface TemplateGroup {
  master: TaskTemplateListItem;
  subtypes: TaskTemplateListItem[];
  firmVariants: TaskTemplateListItem[];
}

function groupTemplatesByHierarchy(templates: TaskTemplateListItem[]): {
  groups: TemplateGroup[];
  ungrouped: TaskTemplateListItem[];
} {
  // Index templates by id for parent lookups
  const byId = new Map(templates.map((t) => [t.id, t]));
  const grouped = new Set<string>();
  const groups: TemplateGroup[] = [];

  // Find masters: jurisdiction_base without case_subtype
  const masters = templates.filter(
    (t) => t.template_kind === 'jurisdiction_base' && !t.case_subtype,
  );

  for (const master of masters) {
    grouped.add(master.id);

    // Subtypes: jurisdiction_variant whose parent is this master
    const subtypes = templates.filter(
      (t) =>
        t.template_kind === 'jurisdiction_variant' &&
        t.parent_template_id === master.id &&
        !grouped.has(t.id),
    );
    subtypes.forEach((s) => grouped.add(s.id));

    // Firm variants: firm_variant whose parent is this master OR one of its subtypes
    const subtypeIds = new Set(subtypes.map((s) => s.id));
    const firmVariants = templates.filter(
      (t) =>
        t.template_kind === 'firm_variant' &&
        (t.parent_template_id === master.id || subtypeIds.has(t.parent_template_id || '')) &&
        !grouped.has(t.id),
    );
    firmVariants.forEach((f) => grouped.add(f.id));

    groups.push({ master, subtypes, firmVariants });
  }

  // Also group orphan subtypes that reference a parent not in the current page
  const orphanSubtypes = templates.filter(
    (t) =>
      t.template_kind === 'jurisdiction_variant' &&
      !grouped.has(t.id),
  );
  // Group orphan firm variants
  const orphanFirmVariants = templates.filter(
    (t) =>
      t.template_kind === 'firm_variant' &&
      !grouped.has(t.id),
  );

  const ungrouped = templates.filter(
    (t) => !grouped.has(t.id) && !orphanSubtypes.includes(t) && !orphanFirmVariants.includes(t),
  );

  return {
    groups,
    ungrouped: [...orphanSubtypes, ...orphanFirmVariants, ...ungrouped],
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const TemplateBrowser: React.FC<TemplateBrowserProps> = ({ onSelectTemplate, onEditTemplate, onArchiveTemplate }) => {
  const [practiceAreas, setPracticeAreas] = useState<Array<{ name: string; displayName: string }>>([]);
  const [filters, setFilters] = useState<TaskTemplateListFilters>({
    source: 'all',
    page: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
  });
  const [templates, setTemplates] = useState<TaskTemplateListItem[]>([]);
  const [pagination, setPagination] = useState<TaskTemplatePagination>(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [forkingTemplateId, setForkingTemplateId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [crawlerStatus, setCrawlerStatus] = useState<CourtIntelligenceStatusResponse | null>(null);
  const [crawlerStatusLoading, setCrawlerStatusLoading] = useState<boolean>(false);
  const [runningManualCrawl, setRunningManualCrawl] = useState<boolean>(false);
  const [generatingTemplate, setGeneratingTemplate] = useState<boolean>(false);
  const [reviewQueueItems, setReviewQueueItems] = useState<CourtReviewQueueItem[]>([]);
  const [reviewQueueTotal, setReviewQueueTotal] = useState<number>(0);
  const [reviewQueueLoading, setReviewQueueLoading] = useState<boolean>(false);
  const [reviewQueueError, setReviewQueueError] = useState<string | null>(null);
  const [reviewQueueActionItemId, setReviewQueueActionItemId] = useState<string | null>(null);
  const [generatePromptJurisdiction, setGeneratePromptJurisdiction] = useState<string | null>(null);
  const [generatingSubtypes, setGeneratingSubtypes] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    const loadPracticeAreas = async () => {
      try {
        const practiceAreaService = PracticeAreaConfigService.getInstance();
        const available = await practiceAreaService.getAvailablePracticeAreas();
        if (mounted) {
          setPracticeAreas(
            available
              .filter((area) => area.isActive)
              .map((area) => ({ name: area.name, displayName: area.displayName || area.name })),
          );
        }
      } catch (err) {
        console.error('Failed to load practice areas for template browser:', err);
      }
    };

    loadPracticeAreas();
    return () => {
      mounted = false;
    };
  }, []);

  const refreshCrawlerStatus = useCallback(async () => {
    setCrawlerStatusLoading(true);
    try {
      const jurisdictionCode = extractJurisdictionCode(filters.jurisdiction);
      const status = await getCourtIntelligenceStatus(jurisdictionCode);
      setCrawlerStatus(status);
    } catch (err) {
      console.error('Failed to load court intelligence status:', err);
    } finally {
      setCrawlerStatusLoading(false);
    }
  }, [filters.jurisdiction]);

  const refreshReviewQueue = useCallback(async () => {
    setReviewQueueLoading(true);
    setReviewQueueError(null);
    try {
      const jurisdictionCode = extractJurisdictionCode(filters.jurisdiction);
      const result = await getCourtIntelligenceReviewQueue({
        jurisdiction_code: jurisdictionCode,
        status: 'pending',
        limit: 25,
      });
      setReviewQueueItems(result.items);
      setReviewQueueTotal(result.total);
    } catch (err) {
      setReviewQueueItems([]);
      setReviewQueueTotal(0);
      setReviewQueueError(err instanceof Error ? err.message : 'Failed to load review queue');
    } finally {
      setReviewQueueLoading(false);
    }
  }, [filters.jurisdiction]);

  useEffect(() => {
    let mounted = true;

    const loadTemplates = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await listTaskTemplatesAdmin(filters);
        if (!mounted) return;
        setTemplates(response.templates);
        setPagination(response.pagination);
      } catch (err) {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load templates');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadTemplates();
    return () => {
      mounted = false;
    };
  }, [filters]);

  useEffect(() => {
    void refreshCrawlerStatus();
    void refreshReviewQueue();
  }, [refreshCrawlerStatus, refreshReviewQueue]);

  const canPageBackward = useMemo(() => pagination.page > 1, [pagination.page]);
  const canPageForward = useMemo(
    () => pagination.totalPages > 0 && pagination.page < pagination.totalPages,
    [pagination.page, pagination.totalPages],
  );

  const existingSystemTemplate = useMemo(() => {
    const jc = extractJurisdictionCode(filters.jurisdiction);
    const pa = (filters.practice_area || '').trim().toLowerCase().replace(/\s+/g, '_');
    if (!jc || !pa) return null;
    return templates.find(
      (t) =>
        t.is_system_template &&
        (t.jurisdiction_code || '').toUpperCase() === jc &&
        (t.practice_area || '').toLowerCase().replace(/\s+/g, '_') === pa &&
        !t.case_subtype,
    ) || null;
  }, [filters.jurisdiction, filters.practice_area, templates]);

  // Hierarchy grouping
  const { groups: templateGroups, ungrouped: ungroupedTemplates } = useMemo(
    () => groupTemplatesByHierarchy(templates),
    [templates],
  );

  const handleCreateVariant = async (templateId: string) => {
    try {
      setForkingTemplateId(templateId);
      setError(null);
      const fork = await forkTaskTemplateAdmin(templateId);
      setSuccessMessage(`Firm variant created: ${fork.name}`);
      onEditTemplate(fork.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create firm variant');
    } finally {
      setForkingTemplateId(null);
    }
  };

  const handleRunManualCrawl = async () => {
    try {
      setRunningManualCrawl(true);
      setError(null);
      setSuccessMessage(null);

      const jurisdictionCode = extractJurisdictionCode(filters.jurisdiction);
      const result = await triggerCourtIntelligenceManualRun({
        jurisdiction_code: jurisdictionCode,
        practice_area: normalizePracticeAreaForExtraction(filters.practice_area),
        tools: ['forms', 'judges', 'fees'],
        no_cache: true,
      });

      const successCount = result.results.filter((item) => item.success).length;
      const failureCount = result.results.length - successCount;
      setSuccessMessage(
        `Manual crawl completed for ${result.jurisdiction_code}: ${successCount} succeeded, ${failureCount} failed.`,
      );

      setFilters((prev) => ({ ...prev }));
      await refreshCrawlerStatus();
      await refreshReviewQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run manual crawl');
    } finally {
      setRunningManualCrawl(false);
    }
  };

  const handleGenerateTemplate = async () => {
    try {
      setGeneratingTemplate(true);
      setError(null);
      setSuccessMessage(null);
      const jurisdictionCode = extractJurisdictionCode(filters.jurisdiction);
      const practiceArea = String(filters.practice_area || '').trim();

      if (!jurisdictionCode) {
        throw new Error('Select a jurisdiction code (e.g., CA-06073) before generating a template.');
      }
      if (!practiceArea) {
        throw new Error('Select a practice area before generating a template.');
      }

      const result = await synthesizeTemplateFromCourtData({
        jurisdiction_code: jurisdictionCode,
        practice_area: practiceArea,
      });

      const action = result.was_updated ? 'Updated' : 'Created';
      setSuccessMessage(
        `${action} template "${result.template.name}" from ${result.evidence.forms_total} forms, ${result.evidence.judges_total} judges, and ${result.evidence.fees_total} fee rows.`,
      );

      setFilters((prev) => ({ ...prev, page: 1 }));
      await refreshCrawlerStatus();
      await refreshReviewQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate jurisdiction template');
    } finally {
      setGeneratingTemplate(false);
    }
  };

  const handleGenerateSubtypes = async () => {
    try {
      setGeneratingSubtypes(true);
      setError(null);
      setSuccessMessage(null);
      const jurisdictionCode = extractJurisdictionCode(filters.jurisdiction);
      const practiceArea = String(filters.practice_area || '').trim();
      if (!jurisdictionCode || !practiceArea) {
        throw new Error('Select both a jurisdiction and practice area before generating subtype templates.');
      }
      const result = await synthesizeSubtypeTemplates({
        jurisdiction_code: jurisdictionCode,
        practice_area: practiceArea,
      });
      const subtypeNames = result.subtypes.map((s) => s.subtype_key).join(', ');
      setSuccessMessage(
        `Generated master + ${result.subtypes.length} subtype templates (${subtypeNames}) for ${practiceArea} [${jurisdictionCode}].`,
      );
      setFilters((prev) => ({ ...prev, page: 1 }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate subtype templates');
    } finally {
      setGeneratingSubtypes(false);
    }
  };

  const handleApproveReviewItem = async (item: CourtReviewQueueItem) => {
    try {
      setReviewQueueActionItemId(item.id);
      setReviewQueueError(null);
      const result = await approveCourtIntelligenceReviewItem(item.id);
      const lifecycleStatus = result.updated_registry?.lifecycle_status || 'updated';
      setSuccessMessage(`Approved review item for ${item.jurisdiction_code}. Registry status: ${lifecycleStatus}.`);
      if (lifecycleStatus === 'validated') {
        setGeneratePromptJurisdiction(item.jurisdiction_code);
      }
      await refreshReviewQueue();
      await refreshCrawlerStatus();
    } catch (err) {
      setReviewQueueError(err instanceof Error ? err.message : 'Failed to approve review item');
    } finally {
      setReviewQueueActionItemId(null);
    }
  };

  const handleRejectReviewItem = async (item: CourtReviewQueueItem) => {
    try {
      setReviewQueueActionItemId(item.id);
      setReviewQueueError(null);
      const result = await rejectCourtIntelligenceReviewItem(item.id);
      const lifecycleStatus = result.updated_registry?.lifecycle_status || 'updated';
      setSuccessMessage(`Rejected review item for ${item.jurisdiction_code}. Registry status: ${lifecycleStatus}.`);
      await refreshReviewQueue();
      await refreshCrawlerStatus();
    } catch (err) {
      setReviewQueueError(err instanceof Error ? err.message : 'Failed to reject review item');
    } finally {
      setReviewQueueActionItemId(null);
    }
  };

  // Render a single template card
  const renderCard = (template: TaskTemplateListItem, indent = false) => (
    <div key={template.id} className={indent ? 'ml-6 border-l-2 border-gray-200 pl-4 dark:border-gray-600' : ''}>
      <TemplateCard
        template={template}
        onViewDetails={onSelectTemplate}
        onCreateVariant={handleCreateVariant}
        onEditTemplate={onEditTemplate}
        onArchiveTemplate={onArchiveTemplate}
        isForking={forkingTemplateId === template.id}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      <CourtIntelligenceWizardPanel
        defaultPracticeArea={filters.practice_area}
        defaultJurisdiction={filters.jurisdiction}
        onRunCompleted={async ({ jurisdiction_code, practice_area }) => {
          const resolvedPracticeArea = resolvePracticeAreaName(practice_area, practiceAreas);
          setFilters((prev) => ({
            ...prev,
            jurisdiction: jurisdiction_code,
            practice_area: resolvedPracticeArea,
            page: 1,
          }));
          await refreshCrawlerStatus();
          await refreshReviewQueue();
        }}
      />

      <TemplateFilterBar filters={filters} practiceAreas={practiceAreas} onChange={setFilters} />

      {/* Crawler status + actions — ABOVE the template list */}
      <CrawlerStatusBar
        templatesCount={templates.length}
        status={crawlerStatus}
        loading={crawlerStatusLoading}
        runningManualRun={runningManualCrawl}
        generatingTemplate={generatingTemplate}
        targetJurisdiction={extractJurisdictionCode(filters.jurisdiction)}
        targetPracticeArea={filters.practice_area}
        existingTemplateName={existingSystemTemplate?.name}
        onRunManual={handleRunManualCrawl}
        onGenerateTemplate={handleGenerateTemplate}
      />

      {/* Generate Subtype Templates prompt */}
      {existingSystemTemplate && !existingSystemTemplate.case_subtype && filters.practice_area && (
        <div className="flex items-center justify-between rounded-lg border border-lex-accent bg-lex-accent-soft px-4 py-3 text-sm text-lex-accent dark:border-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent">
          <span>
            Master template <strong>&ldquo;{existingSystemTemplate.name}&rdquo;</strong> exists.
            Generate subtype workflow templates (Divorce, Custody, Paternity, etc.) for case-specific matter creation.
          </span>
          <button
            type="button"
            onClick={() => void handleGenerateSubtypes()}
            disabled={generatingSubtypes}
            className="ml-3 shrink-0 rounded-md border border-lex-accent bg-white px-3 py-1.5 text-xs font-semibold text-lex-accent hover:bg-lex-state-hover disabled:cursor-not-allowed disabled:opacity-60 dark:border-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent dark:hover:bg-lex-state-hover"
          >
            {generatingSubtypes ? 'Generating Subtypes...' : 'Generate Subtype Templates'}
          </button>
        </div>
      )}

      {/* Post-approval generate prompt */}
      {generatePromptJurisdiction && (
        <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <span>
            <strong>{generatePromptJurisdiction}</strong> approved and validated.
            {existingSystemTemplate
              ? ` System template "${existingSystemTemplate.name}" will be updated with latest court data.`
              : filters.practice_area
                ? ` Ready to create a new ${filters.practice_area} template.`
                : ' Select a practice area, then generate a template.'}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setGeneratePromptJurisdiction(null);
                void handleGenerateTemplate();
              }}
              disabled={generatingTemplate || !filters.practice_area}
              className="rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {generatingTemplate
                ? (existingSystemTemplate ? 'Updating...' : 'Generating...')
                : (existingSystemTemplate ? 'Update Template' : 'Generate Template')}
            </button>
            <button
              type="button"
              onClick={() => setGeneratePromptJurisdiction(null)}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
          {error}
        </div>
      )}

      {/* Template list — grouped by hierarchy */}
      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          Loading templates...
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          No templates matched your current filters.
        </div>
      ) : (
        <div className="space-y-6">
          {/* Grouped templates */}
          {templateGroups.map((group) => (
            <div
              key={group.master.id}
              className="rounded-lg border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-gray-800/50"
            >
              {/* Group header */}
              <div className="mb-3 flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {group.master.jurisdiction_code || 'Generic'} &middot; {group.master.practice_area || 'Unspecified'}
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {1 + group.subtypes.length + group.firmVariants.length} template{group.subtypes.length + group.firmVariants.length > 0 ? 's' : ''}
                </span>
              </div>

              <div className="space-y-3">
                {/* Master template */}
                {renderCard(group.master)}

                {/* Subtypes — indented */}
                {group.subtypes.length > 0 && (
                  <div className="space-y-3">
                    <div className="ml-6 flex items-center gap-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-lex-accent dark:text-lex-accent">
                        Subtype Variants ({group.subtypes.length})
                      </span>
                    </div>
                    {group.subtypes.map((s) => renderCard(s, true))}
                  </div>
                )}

                {/* Firm variants — indented */}
                {group.firmVariants.length > 0 && (
                  <div className="space-y-3">
                    <div className="ml-6 flex items-center gap-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                        Firm Variants ({group.firmVariants.length})
                      </span>
                    </div>
                    {group.firmVariants.map((f) => renderCard(f, true))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Ungrouped templates (no hierarchy match) */}
          {ungroupedTemplates.length > 0 && (
            <div className="space-y-3">
              {templateGroups.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Other Templates
                  </span>
                </div>
              )}
              {ungroupedTemplates.map((t) => renderCard(t))}
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Page {pagination.page} of {Math.max(1, pagination.totalPages)} &middot; {pagination.total} templates
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFilters((prev) => ({ ...prev, page: Math.max(1, (prev.page || 1) - 1) }))}
            disabled={!canPageBackward}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Previous
          </button>
          <button
            onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
            disabled={!canPageForward}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Next
          </button>
        </div>
      </div>

      {/* Review queue at the bottom */}
      <CourtReviewQueuePanel
        items={reviewQueueItems}
        total={reviewQueueTotal}
        loading={reviewQueueLoading}
        error={reviewQueueError}
        actingItemId={reviewQueueActionItemId}
        onRefresh={() => {
          void refreshReviewQueue();
        }}
        onApprove={handleApproveReviewItem}
        onReject={handleRejectReviewItem}
      />
    </div>
  );
};

export default TemplateBrowser;
